import { Calendar, CalendarChangeEvent } from 'primereact/calendar';
import React from 'react';
import styled from 'styled-components';

type IsoDateInputProps = {
  value?: string;
  type?: string;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const parseIsoDate = (value?: string): Date | null => {
  if (!value) return null;

  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day);

  // Reject invalid calendar dates such as 2026-02-31.
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return null;
  }

  return parsed;
};

const toIsoDateString = (value?: Date | null): string => {
  if (!value) return '';

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const IsoDateInput: React.FunctionComponent<IsoDateInputProps> = ({ value, onChange, disabled }) => {
  const externalValue = value ?? '';
  const [optimisticValue, setOptimisticValue] = React.useState<string | undefined>(undefined);
  const previousExternalValueRef = React.useRef<string>(externalValue);

  React.useEffect(() => {
    const previousExternalValue = previousExternalValueRef.current;
    previousExternalValueRef.current = externalValue;

    if (optimisticValue === undefined) return;

    // Clear optimistic value when external state catches up or changes due to server-side normalization/rejection.
    if (externalValue === optimisticValue || externalValue !== previousExternalValue) {
      setOptimisticValue(undefined);
    }
  }, [externalValue, optimisticValue]);

  const effectiveValue = optimisticValue ?? externalValue;

  const emitChange = (nextValue: string) => {
    if (nextValue === effectiveValue) return;

    setOptimisticValue(nextValue);

    onChange?.({
      currentTarget: { value: nextValue },
      target: { value: nextValue },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <StyledCalendar
      value={parseIsoDate(effectiveValue)}
      dateFormat="yy-mm-dd"
      mask="9999-99-99"
      placeholder="YYYY-MM-DD"
      showIcon
      showButtonBar
      showOnFocus={false}
      disabled={disabled}
      onChange={(event: CalendarChangeEvent) => {
        const typedValue = (event.originalEvent?.target as HTMLInputElement | null)?.value?.trim();

        // For typed input, only save when the ISO value is complete and valid.
        if (typeof typedValue === 'string') {
          if (!typedValue || typedValue.replace(/[_-]/g, '') === '') {
            emitChange('');
            return;
          }

          if (typedValue.includes('_')) return;

          const parsedTypedValue = parseIsoDate(typedValue);
          if (!parsedTypedValue) return;

          emitChange(toIsoDateString(parsedTypedValue));
          return;
        }

        // For picker selection, event value is a Date.
        const nextValue = toIsoDateString((event.value as Date | null) ?? null);
        emitChange(nextValue);
      }}
    />
  );
};

const StyledCalendar = styled(Calendar)`
  flex: 1 0 auto;
  margin: 0.5rem 0 1rem 0;
  min-width: 0;
  display: flex;
  border: solid 1px gray;
  border-radius: 2px;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;

  &:hover,
  &:focus-within {
    border: solid 1px gray;
  }

  &:focus-within {
    outline: 1px solid ${(props) => props.theme.colors.primaryBlack};
  }

  .p-inputtext {
    width: 100%;
    padding: 1rem;
    border: none;
    border-radius: 0;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: normal;
    color: ${(props) => props.theme.colors.primaryBlack};
  }

  .p-button {
    border: none;
    border-radius: 0;
    background: white;
    color: #333;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover,
    &:enabled:hover,
    &:focus,
    &:enabled:focus {
      background: #f3f3f3;
      color: ${(props) => props.theme.colors.primaryOrange};
      box-shadow: none;
      outline: none;
    }
  }

  .p-inputtext:enabled:focus {
    box-shadow: none;
    outline: none;
  }

  .p-inputtext:disabled,
  .p-button:disabled {
    background-color: ${(props) => props.theme.colors.elementInputDisabled};
    color: #545454;
  }

  &.p-disabled {
    background-color: ${(props) => props.theme.colors.elementInputDisabled};
  }
`;

export default IsoDateInput;
